import { LineArrowLeft } from '@qr-front/icons/line-arrow-left'
import { cn } from '@qr-front/ui/utils'
import { sectionsListAtom } from 'features/generator/contentFormSections/MenuForm/MenuForm'
import { TQrScreenProps } from 'features/generator/QrScreen/QrScreen'
import { formatMenuListWithDefaultSectionNames } from 'features/generator/utils/menuListFormat'
import { useAtom, useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef } from 'react'

import {
	primaryColorAtom,
	secondaryColorAtom,
} from '@/content-from-sections/client'

import { activeSectionIdAtom } from './MenuScreen'
import { SectionItem } from './SectionItem'

const createRefSetter =
	<T extends HTMLElement>(refArray: React.MutableRefObject<T[]>) =>
	(element: T | null, index: number) => {
		if (element) {
			refArray.current[index] = element
		}
	}

export const MenuTabView = ({ demoMode, previewMode }: TQrScreenProps) => {
	const t = useTranslations()

	const [activeSectionId, setActiveSectionId] = useAtom(activeSectionIdAtom)
	const menuSections = useAtomValue(sectionsListAtom)
	const filteredMenuSections = menuSections.filter((section) => section.visible)
	const menuSectionsWithDefaultNames = formatMenuListWithDefaultSectionNames(
		filteredMenuSections,
		t,
	)

	const isScrollingByClick = useRef(false)
	const contentRef = useRef<HTMLDivElement>(null)
	const sectionRefs = useRef<HTMLDivElement[]>([])
	const tabRefs = useRef<HTMLButtonElement[]>([])
	const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const setRef = createRefSetter(sectionRefs)
	const setTabRef = createRefSetter(tabRefs)

	const primaryColor = useAtomValue(primaryColorAtom)
	const secondaryColor = useAtomValue(secondaryColorAtom)

	const scrollToTabButton = useCallback((index: number) => {
		const tabRef = tabRefs.current[index]
		if (!tabRef) return

		tabRef.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest',
			inline: 'center',
		})
	}, [])

	const scrollToSection = useCallback(
		(index: number) => {
			if (scrollTimeoutRef.current) {
				clearTimeout(scrollTimeoutRef.current)
			}

			isScrollingByClick.current = true

			const sectionRef = sectionRefs.current[index]
			const tabRef = tabRefs.current[index]

			if (sectionRef && contentRef.current) {
				const offset = index === 0 ? 30 : 24
				const sectionTop = sectionRef.getBoundingClientRect().top
				const containerTop = contentRef.current.getBoundingClientRect().top

				const relativeTop = sectionTop - containerTop
				const scrollTarget = contentRef.current.scrollTop + relativeTop - offset

				contentRef.current.scrollTo({
					top: scrollTarget,
					behavior: 'smooth',
				})
			}

			if (tabRef) {
				tabRef.scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
					inline: 'center',
				})
			}

			setActiveSectionId(index)
			scrollTimeoutRef.current = setTimeout(() => {
				isScrollingByClick.current = false
			}, 800)
		},
		[setActiveSectionId],
	)

	const handleBackButtonClick = () => setActiveSectionId(null)

	useEffect(() => {
		if (!contentRef.current) return

		const observer = new IntersectionObserver(
			(entries) => {
				if (isScrollingByClick.current) return

				const visibleEntries = entries.filter(
					(entry) => entry.intersectionRatio > 0.1,
				)

				if (visibleEntries.length === 0) return

				const topEntry = visibleEntries.reduce((closest, entry) => {
					const currentTop = Math.abs(entry.boundingClientRect.top)
					const closestTop = Math.abs(closest.boundingClientRect.top)
					return currentTop < closestTop ? entry : closest
				})

				const index = sectionRefs.current.findIndex(
					(el) => el === topEntry.target,
				)
				if (index !== -1 && index !== activeSectionId) {
					setActiveSectionId(index)
					scrollToTabButton(index)
				}
			},
			{
				root: contentRef.current,
				threshold: [0.1, 0.3, 0.5],
			},
		)

		sectionRefs.current.forEach((section) => {
			if (section) observer.observe(section)
		})

		return () => {
			observer.disconnect()
		}
	}, [activeSectionId, scrollToTabButton, setActiveSectionId])

	useEffect(() => {
		if (!isScrollingByClick.current && activeSectionId !== null) {
			setTimeout(() => {
				if (sectionRefs.current[activeSectionId]) {
					scrollToSection(activeSectionId)
				}
			}, 150)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const hasScroll =
		contentRef.current &&
		contentRef.current.scrollHeight > contentRef.current.clientHeight

	return (
		<div className={cn('flex flex-col', previewMode ? 'h-[506px]' : 'h-dvh')}>
			<div
				className="sticky top-1 z-10 flex w-full flex-col pt-8"
				style={{ backgroundColor: primaryColor }}
			>
				<div
					className="flex items-center gap-2 px-5"
					onClick={handleBackButtonClick}
					style={{ color: secondaryColor }}
				>
					<LineArrowLeft width={16} height={16} />
					<span className="type-body-medium">
						{t('generator__footer__back_button')}
					</span>
				</div>

				<div className="overflow-x-hidden px-5 pt-4">
					<div className="hide-scrollbar flex w-full items-center gap-4 overflow-x-auto">
						{menuSectionsWithDefaultNames.map((section, index) => {
							const isActive = activeSectionId === index

							return (
								<button
									key={index}
									onClick={() => scrollToSection(index)}
									ref={(element) => setTabRef(element, index)}
									className={cn(
										'duration-400 relative h-7 max-w-40 cursor-pointer pb-1 transition-transform',
									)}
									style={{ color: secondaryColor }}
								>
									<span className="type-body-small block overflow-hidden truncate text-ellipsis whitespace-nowrap font-semibold">
										{section.name}
									</span>

									{isActive && (
										<span
											className="absolute bottom-0 left-1/2 h-0.5 w-full -translate-x-1/2 rounded-full transition-all duration-300"
											style={{ backgroundColor: secondaryColor }}
										/>
									)}
								</button>
							)
						})}
					</div>
				</div>
			</div>

			<div
				className={cn(
					'flex-1 overflow-y-auto bg-white px-5 py-6',
					!hasScroll && 'pb-0',
				)}
				ref={contentRef}
			>
				<div className="mx-auto max-w-qr-screen-constrained space-y-6">
					{menuSectionsWithDefaultNames.map((section, index) => {
						return (
							<div key={index} ref={(element) => setRef(element, index)}>
								<SectionItem section={section} demoMode={demoMode} />
							</div>
						)
					})}
				</div>
			</div>
		</div>
	)
}
